import useTranslation from "next-translate/useTranslation"
import { useRouter } from "next/router"
import { useSelector } from "react-redux"
import { Modal, ModalBody, ModalHeader } from "reactstrap"

import { FeedbackRoles, IProcess, IProject, IUser, MembershipRole } from "@api/schema"
import ActionIcon from "@components/common/ActionIcon"
import { AvailableContentItems } from "@components/common/contentmanagement/ContentManagementHelper"
import SinglePostForm from "@components/feedback/SinglePostForm"
import { AppState } from "@redux/reducer"
import { selectProjectMemberRole } from "@redux/reducer/myProjects"
import { EntityType } from "@redux/reduxTypes"
import { useCurrentUser } from "@services/hooks/useCurrentUser"
import { useToggleState } from "@services/hooks/useToggleState"
import { Routes, routeWithParams, useLoginRouteWithRedirectBack } from "@services/routes"
import { isOfEntityType, urlWithoutHash } from "@services/util"
import { MODULE_FEEDBACK_FOR_PROJECTS_AVAILABLE } from "config"


interface IProps {
  /** className for the Icon */
  className?: string
  /** valid contentId regarding the relatedObject, usually of type IProject */
  contentId?: AvailableContentItems
  /** URL for redirecting back after login */
  redirectBack?: string
  /** relatedObject to be commented */
  relatedObject: IProject | IProcess | IUser
}

/**
 * This component provides an Icon that triggers the commenting of an (optional) contentId of a Project or the Project at
 * a whole, Process or User,
 *
 * @todo prüfen, ob ein user zu einer contentId bereits ein Post abgegeben hat: wenn ja: dropdown-menü zum ansehen des posts anbieten
 * @todo wenn ein User keine Kommentier-Rechte hat, dies sichtbar machen
 */
const FeedbackPostPopup: React.FC<IProps> = (props: IProps) => {
  const {
    className,
    contentId,
    relatedObject,
    redirectBack
  } = props
  const { t } = useTranslation("common")
  const router = useRouter()

  const { isAuthenticated, isProcessManager } = useCurrentUser()

  const settings = useSelector((state: AppState) => state.applicationStates.settings)

  const { isTrue: modalOpen, toggle: toggleModal, setIsTrue: setModalOpen } = useToggleState(false)

  /*
   * project is set, if the relatedObject is given and of type IProject
   */
  const project = isOfEntityType(relatedObject, EntityType.Project)
    ? relatedObject as IProject
    : null

  // get the roles for the current user in that project
  const projectRole = useSelector((state: AppState) => selectProjectMemberRole(state, project.id))

  // the icon is not visible, when relatedObject is an IProject
  // with an empty feedbackAllowedFor-Whitelist
  // or if a specific content may be commented without matching settings
  const iconVisible = project && project.feedbackAllowedFor?.length > 0
    && (!contentId || (contentId && settings.detailedComments))

  // the element may be commentable, default: true
  let hasCommentRight = true

  if (project) {
    // it could be commentable, if the user is not logged in, to let him log in
    if (!isAuthenticated) {
      hasCommentRight = true
    }
    else { // if the user is logged in
      // if the users role is in the feedbackAllowedFor-Whitelist: element is commentable
      if ((project.feedbackAllowedFor?.includes(FeedbackRoles.RegisteredUser))
        || (projectRole === MembershipRole.Coordinator && project.feedbackAllowedFor?.includes(FeedbackRoles.Coordinator))
        || (projectRole === MembershipRole.Planner && project.feedbackAllowedFor?.includes(FeedbackRoles.Planner))
        || (projectRole === MembershipRole.Observer && project.feedbackAllowedFor?.includes(FeedbackRoles.Observer))
        || (isProcessManager && project.feedbackAllowedFor?.includes(FeedbackRoles.ProcessManager))) {
        hasCommentRight = true
      }
      else {
        hasCommentRight = false
      }
    }
  }

  // ID of the Icon to be jumped when redirecting the user
  const commentIconId = "comment_" + contentId?.replace(".", "_")
  // link to redirect after Login
  const redirectBackLinkAfterLogin = redirectBack
    ? redirectBack // use redirectBack if set
    : urlWithoutHash(router.asPath) + (contentId ? "#" + commentIconId : "") // current URL + contentId as anchor

  const loginWithRedirectBack = useLoginRouteWithRedirectBack(redirectBackLinkAfterLogin)

  return MODULE_FEEDBACK_FOR_PROJECTS_AVAILABLE && iconVisible && <>
    <ActionIcon
      // open modal, if logged in, otherwise send to login/registration
      onclick={isAuthenticated
        ? hasCommentRight
          ? toggleModal // if the user has comment-rights, show the feedback-modal
          : () => {
            // without comment-rights, send him to team application
            void router.push(routeWithParams(Routes.ProjectApplication, { slug: project.slug || project.id }))
          }
        : () => { // if not logged in, send to login
          void router.push(loginWithRedirectBack)
        }
      }
      title={hasCommentRight
        ? t("goto.addContentComment", { content: contentId ? t(contentId + ".label") : project?.name })
        : t("project.feedbackAllowedFor.onlyAllowedFor",
          {
            roles: (relatedObject as IProject).feedbackAllowedFor?.filter(role => role !== FeedbackRoles.ProcessManager)
              .map((role) => t("feedback.discussion.feedbackAllowedFor." + role)
              ).join(", ")
          })}
      icon={"comment-add"}
      className={className}
    />

    <Modal
      isOpen={modalOpen}
      toggle={toggleModal}
    >
      <ModalHeader
      >
        {t("feedback.post.title")}
      </ModalHeader>
      <hr className="popup__hr" />
      <ModalBody >
        <SinglePostForm
          relatedObject={relatedObject}
          contentId={contentId}
          onSuccessSubmit={() => setModalOpen(false)}
          onCancel={() => setModalOpen(false)}
        />
      </ModalBody>
    </Modal>
  </>
}


export default FeedbackPostPopup